import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import {
  SITE_CONTACT_EMAIL,
  SITE_LEGAL_JURISDICTION,
  SITE_LEGAL_LAST_UPDATED,
  SITE_NAME,
} from '../config/site';

export default function TermsOfServicePage() {
  const title = `Terms of Use | ${SITE_NAME}`;
  const description = `Terms of Use for ${SITE_NAME} websites, apps, and services. Medical disclaimer, eligibility, payments, and governing law (India).`;

  return (
    <>
      <SeoMeta title={title} description={description} path="/terms-of-service" ogType="website" />

      <main className="static-page legal-page">
        <header className="container static-page-hero">
          <h1>Terms of Use</h1>
          <p className="static-page-lead legal-meta">Last updated: {SITE_LEGAL_LAST_UPDATED}.</p>
        </header>

        <article className="container static-page-body legal-body">
          <section aria-label="Introduction">
            <p>
              Welcome to {SITE_NAME} (&ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;us&rdquo;). By accessing or using our website,
              mobile application, or services (&ldquo;Services&rdquo;), you agree to these Terms of Use.
            </p>
            <p>If you do not agree, please do not use the Services.</p>
          </section>

          <section aria-labelledby="nature-heading">
            <h2 id="nature-heading">1. Nature of Service</h2>
            <p>
              {SITE_NAME} provides a nutrition and habit guidance platform designed to help users improve lifestyle and dietary
              habits.
            </p>
            <p>We do <strong>not</strong> provide:</p>
            <ul>
              <li>Medical advice</li>
              <li>Diagnosis or treatment</li>
              <li>Emergency services</li>
            </ul>
            <p>All content is for informational purposes only. You should consult a qualified healthcare professional before making significant dietary changes.</p>
          </section>

          <section aria-labelledby="eligibility-heading">
            <h2 id="eligibility-heading">2. Eligibility</h2>
            <p>You must:</p>
            <ul>
              <li>Be at least 18 years old, or</li>
              <li>Use the service under the supervision of a parent or guardian</li>
            </ul>
          </section>

          <section aria-labelledby="responsibilities-heading">
            <h2 id="responsibilities-heading">3. User Responsibilities</h2>
            <p>You agree to:</p>
            <ul>
              <li>Provide accurate information</li>
              <li>Use the service for personal, non-commercial use</li>
              <li>Not misuse or attempt to disrupt the platform</li>
            </ul>
            <p>You must <strong>not</strong>:</p>
            <ul>
              <li>Reverse engineer the platform</li>
              <li>Use bots or automation in violation of these Terms or our technical policies</li>
              <li>Upload harmful or illegal content</li>
            </ul>
          </section>

          <section aria-labelledby="health-heading">
            <h2 id="health-heading">4. Health Disclaimer</h2>
            <p>
              {SITE_NAME} is <strong>not</strong> a medical service. We do not guarantee weight loss results or specific health
              outcomes. Results vary based on user consistency and lifestyle.
            </p>
          </section>

          <section aria-labelledby="payments-heading">
            <h2 id="payments-heading">5. Payments &amp; Early Access</h2>
            <ul>
              <li>Pricing is subject to change.</li>
              <li>Early access offers (e.g., &ldquo;2 months for the price of 1&rdquo;) are limited-time and non-transferable.</li>
              <li>All payments, once made, are non-refundable unless required by applicable law.</li>
            </ul>
          </section>

          <section aria-labelledby="account-heading">
            <h2 id="account-heading">6. Account &amp; Access</h2>
            <p>We may suspend or terminate accounts if:</p>
            <ul>
              <li>These Terms are violated, or</li>
              <li>Misuse is detected</li>
            </ul>
          </section>

          <section aria-labelledby="ip-heading">
            <h2 id="ip-heading">7. Intellectual Property</h2>
            <p>All content, branding, and software belong to {SITE_NAME}. You may not copy, reproduce, or distribute them without permission.</p>
          </section>

          <section aria-labelledby="liability-heading">
            <h2 id="liability-heading">8. Limitation of Liability</h2>
            <p>To the maximum extent permitted under Indian law, {SITE_NAME} shall not be liable for:</p>
            <ul>
              <li>Any indirect or incidental damages</li>
              <li>Health outcomes resulting from use of the service</li>
            </ul>
          </section>

          <section aria-labelledby="privacy-heading">
            <h2 id="privacy-heading">9. Privacy</h2>
            <p>
              Your use of the Services is also governed by our{' '}
              <Link to="/privacy-policy" rel="nofollow">
                Privacy Policy
              </Link>
              .
            </p>
          </section>

          <section aria-labelledby="law-heading">
            <h2 id="law-heading">10. Governing Law</h2>
            <p>
              These Terms are governed by the laws of India. Jurisdiction: <strong>{SITE_LEGAL_JURISDICTION}</strong>.
            </p>
          </section>

          <section aria-labelledby="changes-heading">
            <h2 id="changes-heading">11. Changes to Terms</h2>
            <p>We may update these Terms at any time. Continued use of the Services after updates constitutes acceptance of the revised Terms, where permitted by law.</p>
          </section>

          <section aria-labelledby="contact-heading">
            <h2 id="contact-heading">Contact</h2>
            <p>
              Email:{' '}
              <a href={`mailto:${SITE_CONTACT_EMAIL}`}>{SITE_CONTACT_EMAIL}</a>
              {' · '}
              <Link to="/contact">Contact form</Link>
            </p>
          </section>

          <footer className="static-page-foot">
            <p>
              <Link to="/privacy-policy" rel="nofollow">
                Privacy Policy
              </Link>
              {' · '}
              <Link to="/">Home</Link>
            </p>
          </footer>
        </article>
      </main>
    </>
  );
}
